import { useState, useRef, useEffect, useCallback } from "react";
import { MessageSquareText, X, Send, Sparkles } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { sendChat } from "../lib/api";

const getSessionId = () => {
  let sid = localStorage.getItem("vl_chat_session");
  if (!sid) {
    sid = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("vl_chat_session", sid);
  }
  return sid;
};

export const ChatWidget = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (open) {
      if (messages.length === 0) {
        setMessages([{ role: "assistant", content: t("chat.greeting") }]);
      }
      scrollToEnd();
      setTimeout(() => inputRef.current?.focus(), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { scrollToEnd(); }, [messages, loading, scrollToEnd]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { reply } = await sendChat(sessionId.current, text, lang);
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: t("chat.error") }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-launcher"
        aria-label={t("chat.title")}
        className="fixed bottom-24 right-6 z-40 w-14 h-14 flex items-center justify-center bg-secondary border border-primary/50 text-primary shadow-lg hover:scale-105 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
      >
        {open ? <X size={22} /> : <MessageSquareText size={22} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-40 right-6 z-40 w-[calc(100vw-3rem)] sm:w-[380px] h-[520px] max-h-[70vh] flex flex-col bg-card/95 backdrop-blur-xl border border-border/70 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border/60 bg-secondary/80">
            <div className="w-9 h-9 flex items-center justify-center bg-primary/15 text-primary rounded-full shrink-0">
              <Sparkles size={17} />
            </div>
            <div className="min-w-0">
              <p className="font-headings text-base leading-tight text-foreground">{t("chat.title")}</p>
              <p className="font-accent text-[10px] uppercase tracking-[0.15em] text-muted-foreground truncate">{t("chat.subtitle")}</p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-4 py-2.5 text-sm font-body font-light leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-t-2xl rounded-bl-2xl"
                      : "bg-secondary text-foreground/90 border border-border/50 rounded-t-2xl rounded-br-2xl"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-secondary border border-border/50 rounded-t-2xl rounded-br-2xl px-4 py-3 flex gap-1.5">
                  <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border/60 p-3 bg-secondary/60">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="chat-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("chat.placeholder")}
                className="flex-1 resize-none bg-background border border-border/60 px-3 py-2.5 text-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 max-h-28"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                data-testid="chat-send"
                aria-label={t("chat.send")}
                className="w-10 h-10 shrink-0 flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="mt-2 text-[10px] text-muted-foreground text-center font-body">{t("chat.disclaimer")}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
